import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Modal, Form, Alert, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PermissionWrapper from "../components/PermissionWrapper";
import { apiGet, apiPost, apiPut, apiDelete } from "../services/BaseService";
import { validateRequired, validatePhone, validatePinCode, validateAddress } from "../utils/validation";
import PackageService, { GroupCredits } from "../services/PackageService";

interface Schedule {
  id: string;
  playTitle: string;
  playId: string;
  venue: string;
  date: string;
  time: string;
  duration: number;
  ticketPrice: number;
  availableSeats: number;
  totalSeats: number;
  status: string;
  organizerName?: string;
  organizerPhone?: string;
}

const ManageSchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [programs, setPrograms] = useState<{id: string, title: string}[]>([]);
  const [userGroups, setUserGroups] = useState<{_id: string, name: string}[]>([]);
  const [countries, setCountries] = useState<{_id: string, name: string, code: string}[]>([]);
  const [states, setStates] = useState<{_id: string, name: string}[]>([]);
  
  // Modal form states for country/state
  const [modalCountry, setModalCountry] = useState<string>("");
  const [modalStates, setModalStates] = useState<{stateCode: string, name: string}[]>([]);

  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [groupCredits, setGroupCredits] = useState<GroupCredits | null>(null);
  
  // Modal form states
  const [modalGroup, setModalGroup] = useState<string>("");
  const [modalPrograms, setModalPrograms] = useState<{id: string, title: string}[]>([]);
  const [formData, setFormData] = useState({
    programId: "",
    startDate: "",
    endDate: "",
    timing: "",
    address: "",
    pinCode: "",
    state: "",
    country: "",
    organizerName: "",
    organizerPhone: "",
    upiId: "",
    upiName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountHolderName: ""
  });

  useEffect(() => {
    fetchUserGroups();
    fetchCountries();
  }, []);



  useEffect(() => {
    if (selectedGroup) {
      fetchPrograms();
      fetchGroupCredits();
      setSelectedProgram("");
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedProgram) {
      fetchSchedules();
    } else {
      setSchedules([]);
    }
  }, [selectedProgram, currentPage]);

  useEffect(() => {
    if (modalGroup) {
      fetchModalPrograms();
    }
  }, [modalGroup]);

  useEffect(() => {
    if (modalCountry) {
      fetchModalStates();
    }
  }, [modalCountry]);

  const fetchUserGroups = async () => {
    try {
      const response = await apiGet('/programs/user-groups');
      setUserGroups(response.groups || []);
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiGet('/countries');
      setCountries(response || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryCode: string) => {
    try {
      const response = await apiGet(`/states/country/${countryCode}`);
      setStates(response || []);
    } catch (error) {
      console.error('Failed to fetch states:', error);
      setStates([]);
    }
  };

  const fetchModalStates = async () => {
    try {
      const response = await apiGet(`/states/country/${modalCountry}`);
      setModalStates(response || []);
    } catch (error) {
      console.error('Failed to fetch modal states:', error);
      setModalStates([]);
    }
  };

  const fetchSchedules = async () => {
    if (!selectedProgram) return;
    setLoading(true);
    try {
      const response = await apiGet(`/programschedules/program/${selectedProgram}`);
      const data = Array.isArray(response) ? response : [];
      const selectedProgramTitle = programs.find(p => p.id === selectedProgram)?.title || 'Unknown Program';
      const formattedSchedules = data.map((schedule: any) => ({
        id: schedule._id,
        playTitle: schedule.programId?.title || selectedProgramTitle,
        playId: schedule.programId?._id || schedule.programId || '',
        venue: schedule.address || 'No venue specified',
        date: schedule.startDate,
        time: schedule.timing,
        duration: 120,
        ticketPrice: 0,
        availableSeats: 100,
        totalSeats: 100,
        status: 'Active',
        organizerName: schedule.organizerName || '',
        organizerPhone: schedule.organizerPhone || ''
      }));
      setSchedules(formattedSchedules);
      setTotalPages(Math.ceil(formattedSchedules.length / 10));
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
    setLoading(false);
  };

  const fetchPrograms = async () => {
    try {
      const response = await apiGet(`/programs/group/${selectedGroup}`);
      const programsData = response.programs || response;
      const formattedPrograms = programsData.map((program: any) => ({
        id: program._id,
        title: program.title
      }));
      setPrograms(formattedPrograms);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchModalPrograms = async () => {
    try {
      const response = await apiGet(`/programs/group/${modalGroup}`);
      const programsData = response.programs || response;
      const formattedPrograms = programsData.map((program: any) => ({
        id: program._id,
        title: program.title
      }));
      setModalPrograms(formattedPrograms);
    } catch (error) {
      console.error('Error fetching modal programs:', error);
    }
  };

  const fetchGroupCredits = async () => {
    try {
      if (!selectedGroup) return;
      const data = await PackageService.getGroupCredits(selectedGroup);
      setGroupCredits(data);
    } catch (error) {
      console.error('Error fetching group credits:', error);
    }
  };

  const handleEdit = async (schedule: Schedule) => {
    try {
      const response = await apiGet(`/programschedules/${schedule.id}`);
      const fullSchedule = response;
      
      const selectedProgramTitle = programs.find(p => p.id === selectedProgram)?.title || 'Unknown Program';
      const updatedSchedule = {
        ...schedule,
        playTitle: fullSchedule.programId?.title || selectedProgramTitle
      };
      
      setSelectedSchedule(updatedSchedule);
      
      setFormData({
        programId: fullSchedule.programId?._id || fullSchedule.programId || "",
        startDate: fullSchedule.startDate ? new Date(fullSchedule.startDate).toISOString().split('T')[0] : "",
        endDate: fullSchedule.endDate ? new Date(fullSchedule.endDate).toISOString().split('T')[0] : "",
        timing: fullSchedule.timing || "",
        address: fullSchedule.address || "",
        pinCode: fullSchedule.pinCode || "",
        state: fullSchedule.state || "",
        country: fullSchedule.country || "",
        organizerName: fullSchedule.organizerName || "",
        organizerPhone: fullSchedule.organizerPhone || "",
        upiId: fullSchedule.upiId || "",
        upiName: fullSchedule.upiName || "",
        bankName: fullSchedule.bankName || "",
        accountNumber: fullSchedule.accountNumber || "",
        ifscCode: fullSchedule.ifscCode || "",
        accountHolderName: fullSchedule.accountHolderName || ""
      });
      
      setModalGroup(selectedGroup);
      setModalCountry(fullSchedule.country || "");
      
      // Fetch states for the selected country
      if (fullSchedule.country) {
        const selectedCountry = countries.find(c => c._id === fullSchedule.country);
        if (selectedCountry?.code) {
          const response = await apiGet(`/states/country/${selectedCountry.code}`);
          setModalStates(response || []);
        }
      }
      
    } catch (error) {
      console.error('Error fetching schedule details:', error);
      setSelectedSchedule(schedule);
    }
    
    setValidationErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        await apiDelete(`/programschedules/${id}`);
        setSchedules(schedules.filter(schedule => schedule.id !== id));
      } catch (error: any) {
        if (error.response?.status === 400) {
          alert(error.response.data.error || "Cannot delete schedule with tickets. Please remove all tickets first.");
        } else {
          alert('Error deleting schedule. Please try again.');
        }
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const handleAddNew = () => {
    setSelectedSchedule(null);
    setModalGroup("");
    setModalCountry("");
    setModalStates([]);
    setFormData({
      programId: "",
      startDate: "",
      endDate: "",
      timing: "",
      address: "",
      pinCode: "",
      state: "",
      country: "",
      organizerName: "",
      organizerPhone: "",
      upiId: "",
      upiName: "",
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      accountHolderName: ""
    });
    setValidationErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    // Required field validations
    if (!formData.programId && !selectedSchedule) {
      const programValidation = validateRequired(formData.programId, 'Program');
      if (!programValidation.isValid) errors.programId = programValidation.message!;
    }
    
    const startDateValidation = validateRequired(formData.startDate, 'Start Date');
    if (!startDateValidation.isValid) errors.startDate = startDateValidation.message!;
    
    const timingValidation = validateRequired(formData.timing, 'Timing');
    if (!timingValidation.isValid) errors.timing = timingValidation.message!;
    
    const addressValidation = validateAddress(formData.address);
    if (!addressValidation.isValid) errors.address = addressValidation.message!;
    else if (!formData.address) errors.address = 'Address is required';
    
    const pinCodeValidation = validatePinCode(formData.pinCode);
    if (!pinCodeValidation.isValid) errors.pinCode = pinCodeValidation.message!;
    else if (!formData.pinCode) errors.pinCode = 'Pin code is required';
    
    const countryValidation = validateRequired(formData.country, 'Country');
    if (!countryValidation.isValid) errors.country = countryValidation.message!;
    
    const stateValidation = validateRequired(formData.state, 'State');
    if (!stateValidation.isValid) errors.state = stateValidation.message!;
    
    // Optional field validations
    if (formData.organizerPhone) {
      const phoneValidation = validatePhone(formData.organizerPhone);
      if (!phoneValidation.isValid) errors.organizerPhone = phoneValidation.message!;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveSchedule = async () => {
    if (!validateForm()) return;
    
    // Check schedule credits for new schedules
    if (!selectedSchedule && groupCredits && (groupCredits.remainingCredits?.scheduleCredits ?? 0) <= 0) {
      alert('Insufficient schedule credits! Please purchase additional credits to create new schedules.');
      return;
    }
    
    try {
      setSaving(true);
      const scheduleData = {
        ...formData,
        groupId: selectedSchedule ? undefined : selectedGroup
      };
      
      if (selectedSchedule) {
        await apiPut(`/programschedules/${selectedSchedule.id}`, scheduleData);
      } else {
        await apiPost('/programschedules', scheduleData);
        // Decrement schedule credits after creating new schedule
        // eslint-disable-next-line react-hooks/rules-of-hooks
        await PackageService.useCredits(selectedGroup, 1, 0);
      }
      
      // Refresh credits and schedules after any operation
      await fetchGroupCredits();
      await fetchSchedules();
      setShowModal(false);
      setValidationErrors({});
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Error saving schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <PermissionWrapper pageName="ManageSchedulePage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h2 className="mb-3 mb-md-0">
                <i className="bi bi-calendar-event me-2"></i>
                My Schedules
              </h2>
            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mb-4">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Group</Form.Label>
              <Form.Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                <option value="">Choose Group...</option>
                {userGroups.map(group => (
                  <option key={group._id} value={group._id}>{group.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Program/Play</Form.Label>
              <Form.Select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)} disabled={!selectedGroup}>
                <option value="">Choose Program...</option>
                {programs.map(program => (
                  <option key={program.id} value={program.id}>{program.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button 
              variant="primary" 
              onClick={handleAddNew}
              disabled={!selectedGroup || (groupCredits?.remainingCredits?.scheduleCredits ?? 0) <= 0}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Add Schedule
            </Button>
            {groupCredits && (groupCredits.remainingCredits?.scheduleCredits ?? 0) <= 0 && (
              <small className="text-danger ms-2">No schedule credits remaining</small>
            )}
          </Col>
        </Row>

        {/* Credits Display */}
        {selectedGroup && groupCredits && (
          <Row className="mb-3">
            <Col>
              <Alert variant={(groupCredits.remainingCredits?.scheduleCredits ?? 0) > 0 ? 'success' : 'warning'} className="d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-calendar-check me-2"></i>
                  <strong>Schedule Credits:</strong> {groupCredits.remainingCredits?.scheduleCredits ?? 0} remaining out of {groupCredits.totalCredits?.scheduleCredits ?? 0}
                </span>
                {(groupCredits.remainingCredits?.scheduleCredits ?? 0) <= 0 && (
                  <Button variant="outline-primary" size="sm" onClick={() => navigate('/dashboard/package-selection')}>
                    <i className="bi bi-plus-circle me-1"></i>Add Credits
                  </Button>
                )}
              </Alert>
            </Col>
          </Row>
        )}

        {!selectedProgram && (
          <Row className="mb-3">
            <Col>
              <Alert variant="info" className="text-center">
                <i className="bi bi-info-circle me-2"></i>
                Please select Group → Program to view schedules.
              </Alert>
            </Col>
          </Row>
        )}

        {selectedProgram && (
          <Row>
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Schedules for {programs.find(p => p.id === selectedProgram)?.title}</h5>
                  {loading && <div className="spinner-border spinner-border-sm" />}
                </Card.Header>
                <Card.Body className="p-0">
                  {schedules.length > 0 ? (
                    <>
                      <div className="p-3">
                        <Row className="g-3">
                          {schedules.map((schedule) => (
                            <Col xs={12} sm={6} lg={4} key={schedule.id}>
                              <Card className="h-100 shadow-sm">
                                <Card.Body>
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="text-primary mb-0">{schedule.playTitle}</h6>
                                    <div className="d-flex gap-1">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleEdit(schedule)}
                                        title="Edit Schedule"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDelete(schedule.id)}
                                        title="Delete Schedule"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="mb-2">
                                    <i className="bi bi-calendar me-2 text-muted"></i>
                                    <strong>{formatDate(schedule.date)}</strong>
                                  </div>
                                  <div className="mb-2">
                                    <i className="bi bi-clock me-2 text-muted"></i>
                                    {schedule.time}
                                  </div>
                                  <div className="mb-0">
                                    <i className="bi bi-geo-alt me-2 text-muted"></i>
                                    <small className="text-muted">{schedule.venue}</small>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                      {totalPages > 1 && (
                        <div className="d-flex justify-content-center p-3">
                          <Pagination>
                            <Pagination.Prev 
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(currentPage - 1)}
                            />
                            {[...Array(totalPages)].map((_, i) => (
                              <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => setCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </Pagination.Item>
                            ))}
                            <Pagination.Next 
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(currentPage + 1)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center p-4">
                      <i className="bi bi-calendar-x display-1 text-muted"></i>
                      <h5 className="mt-3 text-muted">No schedules found</h5>
                      <p className="text-muted">Create a new schedule for this program.</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Schedule Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedSchedule ? "Edit Schedule" : "Add New Schedule"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              {/* Program Selection */}
              {selectedSchedule ? (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Program</Form.Label>
                      <Form.Control
                        type="text"
                        value={selectedSchedule.playTitle}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Group</Form.Label>
                      <Form.Select value={modalGroup} onChange={(e) => setModalGroup(e.target.value)}>
                        <option value="">Choose Group...</option>
                        {userGroups.map(group => (
                          <option key={group._id} value={group._id}>{group.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Program <span className="text-danger">*</span></Form.Label>
                      <Form.Select 
                        value={formData.programId} 
                        onChange={(e) => {
                        setFormData({...formData, programId: e.target.value});
                        if (validationErrors.programId) {
                          setValidationErrors(prev => ({ ...prev, programId: '' }));
                        }
                      }} 
                        disabled={!modalGroup}
                        isInvalid={!!validationErrors.programId}
                      >
                        <option value="">Choose Program...</option>
                        {modalPrograms.map(program => (
                          <option key={program.id} value={program.id}>{program.title}</option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.programId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              
              {/* Schedule Details */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => {
                        setFormData({...formData, startDate: e.target.value});
                        if (validationErrors.startDate) {
                          setValidationErrors(prev => ({ ...prev, startDate: '' }));
                        }
                      }}
                      isInvalid={!!validationErrors.startDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.startDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Timing <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.timing}
                      onChange={(e) => {
                        setFormData({...formData, timing: e.target.value});
                        if (validationErrors.timing) {
                          setValidationErrors(prev => ({ ...prev, timing: '' }));
                        }
                      }}
                      isInvalid={!!validationErrors.timing}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.timing}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pin Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter pin code"
                      value={formData.pinCode}
                      onChange={(e) => {
                        setFormData({...formData, pinCode: e.target.value});
                        if (validationErrors.pinCode) {
                          setValidationErrors(prev => ({ ...prev, pinCode: '' }));
                        }
                      }}
                      isInvalid={!!validationErrors.pinCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.pinCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter venue address"
                      value={formData.address}
                      onChange={(e) => {
                        setFormData({...formData, address: e.target.value});
                        if (validationErrors.address) {
                          setValidationErrors(prev => ({ ...prev, address: '' }));
                        }
                      }}
                      isInvalid={!!validationErrors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={modalCountry}
                      onChange={(e) => {
                        setModalCountry(e.target.value);
                        setFormData({...formData, country: e.target.value, state: ""});
                        if (validationErrors.country) {
                          setValidationErrors(prev => ({ ...prev, country: '' }));
                        }
                      }}
                      isInvalid={!!validationErrors.country}
                    >
                      <option value="">Select Country...</option>
                      <option value="IN">India</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.country}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={formData.state}
                      onChange={(e) => {
                        setFormData({...formData, state: e.target.value});
                        if (validationErrors.state) {
                          setValidationErrors(prev => ({ ...prev, state: '' }));
                        }
                      }}
                      disabled={!modalCountry}
                      isInvalid={!!validationErrors.state}
                    >
                      <option value="">Select State...</option>
                      {modalStates.map(state => (
                        <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.state}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organizer Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter organizer name"
                      value={formData.organizerName}
                      onChange={(e) => setFormData({...formData, organizerName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organizer Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter organizer phone"
                      value={formData.organizerPhone}
                      onChange={(e) => {
                        setFormData({...formData, organizerPhone: e.target.value});
                        if (validationErrors.organizerPhone) {
                          setValidationErrors(prev => ({ ...prev, organizerPhone: '' }));
                        }
                      }}
                      isInvalid={!!validationErrors.organizerPhone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.organizerPhone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Payment Details */}
              <hr />
              <h6 className="mb-3">Payment Details</h6>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>UPI ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter UPI ID (e.g., user@paytm)"
                      value={formData.upiId}
                      onChange={(e) => setFormData({...formData, upiId: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>UPI Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter UPI account holder name"
                      value={formData.upiName}
                      onChange={(e) => setFormData({...formData, upiName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter bank name"
                      value={formData.bankName}
                      onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account number"
                      value={formData.accountNumber}
                      onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>IFSC Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter IFSC code"
                      value={formData.ifscCode}
                      onChange={(e) => setFormData({...formData, ifscCode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account Holder Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account holder name"
                      value={formData.accountHolderName}
                      onChange={(e) => setFormData({...formData, accountHolderName: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveSchedule} disabled={saving}>
              {saving ? (
                <><div className="spinner-border spinner-border-sm me-2" role="status"></div>Saving...</>
              ) : (
                <>{selectedSchedule ? "Update" : "Create"} Schedule</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </PermissionWrapper>
  );
};

export default ManageSchedulePage;